class FGProductAvailability extends HTMLElement {
  addToCartText = 'Add to Cart';
  oosText = 'Out of Stock';

  constructor() {
    super();
    if (this.dataset.addToCartText) {
      this.addToCartText = this.dataset.addToCartText
    }
    if (this.dataset.oosText) {
      this.oosText = this.dataset.oosText;
    }
  }

  connectedCallback() {
    this.checkState();

    this.handleStateChangeEvent = this.handleStateChangeEvent.bind(this);
    document.addEventListener('fgw_state_change', this.handleStateChangeEvent);
  }

  disconnectedCallback() {
    document.removeEventListener('fgw_state_change', this.handleStateChangeEvent);
  }

  checkState() {
    // Don't run if shopify is already going to disable the buttons.
    if (this.dataset.canAddToCart === "false") {
      return;
    }
    const productForm = this.findNearestProductForm();
    if (!productForm || !(productForm instanceof HTMLElement)) return;

    // Remove myself if I'm in the quick add block inside the fg-buy-buttons.
    // fg-quick-add can be used outside of fg-buy-buttons, and fg-buy-buttons can be used without fg-quick-add. 
    // When we use fg-buy buttons and have quick add enabled, we don't want two availability widgets.
    const availabilityWidgets = productForm.querySelectorAll('fg-product-availability');
    if (availabilityWidgets.length > 1 && this.dataset.location === "quickadd") {
      // Calling Node.remove() triggers disconnectedCallback()
      this.remove();
      return;
    }


    const currentState = globalThis.FGAvailabilityData.getCurrentState();
    const currentWH = globalThis.FGAvailabilityData.getCurrentWarehouse();

    const productStates = this.dataset.productStates;
    const productOosWH = this.dataset.productOosWh;
    let productTags = [];
    if (this.dataset.productTags) {
      try {
        productTags = JSON.parse(this.dataset.productTags);
      } catch (e) {
        console.warn('FGProductAvailability:: Failed to parse product tags', e);
      }
    }
    const productType = this.dataset.productType;
    const productIsGiftCard = this.dataset.productIsGiftCard;

    const isGiftCard = () => {
      if (productIsGiftCard === "true") {
        return true
      }
      if (productType && productType.toLowerCase() === 'gift card') {
        return true;
      }
    }

    // Skip all checks if this product is a gift card
    if (isGiftCard()) {
      this.enableButtons();
      this.setErrors([]);
      return;
    }

    if (globalThis.FGAvailabilityData.isNoShipState()) {
      this.disableButtons();
      this.setErrors([`Sorry, we do not ship to ${currentState}`]);
      return;
    }

    if (!currentState) {
      this.disableButtons();
      this.setErrors([`Please select a state to ship the order`]);
      return;
    }

    if (!currentWH) {
      this.disableButtons();
      this.setErrors([`Please select a different state to ship the order. No warehouse found for state "${currentState}"`]);
      return;
    }

    const isValidState = productStates?.split(',').includes(currentState);
    if (!isValidState) {
      this.disableButtons();
      this.setErrors([`Sorry, we cannot ship this product to ${currentState}`]);
      return;
    }

    const isOosAtWarehouse = productOosWH?.split(',').includes(currentWH?.toLowerCase())
    if (isOosAtWarehouse) {
      this.disableButtons(true);
      this.setErrors([`Sorry, this product is out of stock in ${currentState}`]);
      return;
    }

    this.enableButtons();
    this.setErrors([]);
  }

  /**
   *
   * @param {Event} event
   */
  handleStateChangeEvent(event) {
    if (!(event instanceof CustomEvent)) return;
    this.checkState();
  }

  findNearestProductCard() {
    return this.closest('product-card');
  }

  findNearestProductInformation() {
    return this.closest('.product-information');
  }

  findNearestProductForm() {
    return this.closest('form.shopify-product-form');
  }

  /**
   * 
   * @param {HTMLElement} productInformation
   * @returns {NodeListOf<HTMLInputElement>}
   */
  getVariantSelectors(productInformation) {
    return productInformation.querySelectorAll('variant-picker input');
  }

  /**
   * 
   * @param {HTMLElement} productForm 
   * @returns {(HTMLButtonElement | HTMLInputElement)[]}
   */
  getElements(productForm) {
    const inputs = [];

    const quickAddButton = productForm.querySelector('quick-add-component button');
    if (quickAddButton && quickAddButton instanceof HTMLButtonElement) {
      inputs.push(quickAddButton);
    }

    const atcButton = productForm.querySelector('add-to-cart-component button.add-to-cart-button');
    if (atcButton && atcButton instanceof HTMLButtonElement) {
      inputs.push(atcButton);
    }

    const quantitySelector = productForm.querySelector('quantity-selector-component');
    if (quantitySelector && quantitySelector instanceof HTMLElement) {
      const plusButton = quantitySelector.querySelector('button[name="plus"]');
      if (plusButton && plusButton instanceof HTMLButtonElement) {
        inputs.push(plusButton);
      }
      const qtyInput = quantitySelector.querySelector('input[name="quantity"]');
      if (qtyInput && qtyInput instanceof HTMLInputElement) {
        inputs.push(qtyInput);
        if (parseInt(qtyInput.value, 10) > 1) {
          // Only pass the minus button for enable/ disable if qty > 1, otherwise
          // use it's default state (which is disabled for qty === 1)
          const minusButton = quantitySelector.querySelector('button[name="minus"]');
          if (minusButton && minusButton instanceof HTMLButtonElement) {
            inputs.push(minusButton);
          }
        }
      }
    }

    const quickViewButton = productForm.closest('product-card')?.querySelector('dialog-component.popup-link')?.querySelector('button');
    if (quickViewButton && quickViewButton instanceof HTMLButtonElement) {
      inputs.push(quickViewButton);
    }

    return inputs;
  }

  /**
   * 
   * @param {HTMLElement} productForm 
   */
  setBuyButtonText(productForm, outOfStock = false) {
    const quickAddButton = productForm.querySelector('quick-add-component button');
    if (quickAddButton && quickAddButton instanceof HTMLButtonElement) {
      const buttonText = quickAddButton.querySelector('.add-to-cart-text__content');
      if (buttonText) {
        if (outOfStock) {
          buttonText.textContent = this.oosText;
        } else {
          buttonText.textContent = this.addToCartText;
        }
      }
    }

    const atcButton = productForm.querySelector('add-to-cart-component button.add-to-cart-button');
    if (atcButton && atcButton instanceof HTMLButtonElement) {
      const buttonText = atcButton.querySelector('.add-to-cart-text__content');
      if (buttonText) {
        if (outOfStock) {
          buttonText.textContent = this.oosText;
        } else {
          buttonText.textContent = this.addToCartText;
        }
      }
    }
  }

  disableButtons(outOfStock = false) {
    const productForm = this.findNearestProductForm();
    if (!productForm || !(productForm instanceof HTMLElement)) return;

    const productInfo = this.findNearestProductInformation();
    if (productInfo && productInfo instanceof HTMLElement) {
      const variantButtons = this.getVariantSelectors(productInfo);
      variantButtons.forEach(button => {
        button.setAttribute('aria-disabled', "true");
        button.setAttribute('data-option-available', "false");
        button.disabled = true;
      });
    }
    this.setAttribute('data-disabled', 'true');

    const elements = this.getElements(productForm);
    elements.forEach(element => {
      element.disabled = true;
    });

    this.setBuyButtonText(productForm, outOfStock);

    document.dispatchEvent(new CustomEvent('fg_product_disable', {
      detail: {
        reason: 'state',
        nearestProductForm: productForm
      }
    }));
  }

  enableButtons() {
    const productForm = this.findNearestProductForm();
    if (!productForm || !(productForm instanceof HTMLElement)) return;

    const productInfo = this.findNearestProductInformation();
    if (productInfo && productInfo instanceof HTMLElement) {
      const variantButtons = this.getVariantSelectors(productInfo);
      variantButtons.forEach(button => {
        button.setAttribute('aria-disabled', "false");
        button.setAttribute('data-option-available', "true");
        button.disabled = false;
      });
    }

    this.setAttribute('data-disabled', 'false');

    const elements = this.getElements(productForm);
    elements.forEach(element => {
      element.disabled = false;
    });

    this.setBuyButtonText(productForm);

    document.dispatchEvent(new CustomEvent('fg_product_enable', {
      detail: {
        reason: 'state',
        nearestProductForm: productForm
      }
    }));
  }

  /**
   * 
   * @param {string[]} errors 
   */
  setErrors(errors) {
    const newErrorEls = errors.map(error => {
      const newEl = document.createElement('span');
      newEl.classList.add('availability-error');
      newEl.textContent = error;
      return newEl;
    });
    const errorEl = this.querySelector('[data-errors]');
    if (!errorEl) return;
    errorEl.innerHTML = "";
    errorEl.append(...newErrorEls);
  }

}

customElements.define('fg-product-availability', FGProductAvailability);